import { auth, USE_EMULATORS } from '../firebase/config';

const API_URL = process.env.REACT_APP_BACKUP_API_URL || '/api/backup.php';
export const MAX_BACKUP_SIZE = 15 * 1024 * 1024;
export const BACKUP_ACCEPT = 'image/jpeg,image/png,image/webp,application/pdf';
// Attachments (Módulo 4 del spec del cliente) también acepta DOCX — el
// resto de tipos (check/payment/closing statement) se quedan con
// BACKUP_ACCEPT tal cual, para no ampliar sin necesidad lo que ya
// funcionaba. Ver backup.php: el backend detecta un .docx por su firma ZIP
// + la extensión del nombre, porque finfo no le da un mime "bonito".
export const ATTACHMENT_ACCEPT = `${BACKUP_ACCEPT},application/vnd.openxmlformats-officedocument.wordprocessingml.document`;

// Tope de espera de una subida. Hostinger a veces corta a mitad de camino los
// envíos de más de ~2 MB y la petición nunca termina: sin este tope, el botón
// se quedaba en "Subiendo…" para siempre. Un archivo de 15 MB tarda menos que esto.
export const UPLOAD_TIMEOUT_MS = 90 * 1000;
export const UPLOAD_TIMEOUT_MESSAGE = 'La subida tardó demasiado y se canceló. Si el archivo pesa más de 2 MB, comprímelo e inténtalo de nuevo.';

const getToken = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error('Debes iniciar sesión para acceder a respaldos.');
  return user.getIdToken();
};

const parseResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.error || 'No se pudo completar la operación de respaldo.');
  return payload;
};

export const validateBackupFile = (file, accept = BACKUP_ACCEPT) => {
  if (!file) return;
  if (file.size > MAX_BACKUP_SIZE) throw new Error('El archivo excede el límite de 15 MB.');
  // Un .docx no siempre llega con el mime "bonito" (algunos navegadores lo
  // mandan como application/zip, igual que el backend lo re-detecta en
  // backup.php) — se acepta también por extensión cuando el accept incluye
  // docx, para no rechazar en el cliente algo que el servidor sí va a aceptar.
  const isDocxByName = accept.includes('wordprocessingml') && /\.docx$/i.test(file.name || '');
  if (!accept.split(',').includes(file.type) && !isDocxByName) {
    throw new Error('Tipo de archivo no permitido.');
  }
};

// En el emulador no hay PHP: se simula la respuesta para poder probar las
// pantallas completas (el archivo no se guarda en ningún lado).
const simulatedBackup = (file, claimId, type) => {
  const hex = Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
  const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
  return { storageKey: `${claimId}/${type}/${hex}.${ext}`, name: file.name, mimeType: file.type, size: file.size, uploadedAt: new Date().toISOString(), uploadedBy: auth.currentUser?.email || '', simulated: true };
};

export const uploadBackup = async ({ file, claimId, invoiceNumber, type }) => {
  validateBackupFile(file);
  if (USE_EMULATORS) return simulatedBackup(file, claimId, type);
  const token = await getToken();
  const body = new FormData();
  body.append('file', file);
  body.append('claimId', claimId);
  body.append('invoiceNumber', invoiceNumber || '');
  body.append('type', type);

  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
  try {
    const response = await fetch(API_URL, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body,
      signal: controller.signal
    });
    return await parseResponse(response);
  } catch (error) {
    if (error?.name === 'AbortError') throw new Error(UPLOAD_TIMEOUT_MESSAGE);
    // Conexión cortada o sin internet: fetch solo dice "Failed to fetch".
    if (error instanceof TypeError) throw new Error('Se perdió la conexión durante la subida. Inténtalo de nuevo; si el archivo pesa más de 2 MB, comprímelo primero.');
    throw error;
  } finally {
    clearTimeout(timer);
  }
};

// Descarga el archivo respaldado como Blob (para armar paquetes, p. ej. el
// expediente para el abogado). Mismas reglas de acceso que openBackup.
export const fetchBackupBlob = async (storageKey) => {
  const token = await getToken();
  const response = await fetch(`${API_URL}?file=${encodeURIComponent(storageKey)}`, {
    headers: { Authorization: `Bearer ${token}` }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    throw new Error(payload.error || `HTTP ${response.status}`);
  }
  return response.blob();
};

// Guarda el archivo respaldado en la computadora del usuario. Mismas reglas de
// acceso que openBackup; `fileName` es el nombre con el que se guarda.
export const downloadBackup = async (storageKey, fileName) => {
  const blobUrl = URL.createObjectURL(await fetchBackupBlob(storageKey));
  const link = document.createElement('a');
  link.href = blobUrl;
  link.download = fileName || storageKey.split('/').pop() || 'respaldo';
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
};

export const openBackup = async (storageKey) => {
  // Se abre la pestaña ANTES del await: hacerlo después de esperar el fetch
  // rompe el "gesto de usuario" del click en varios navegadores (Safari,
  // a veces Chrome) y el popup se bloquea en silencio, sin ningún error.
  // Sin 'noopener' para poder navegar la pestaña una vez llega el blob.
  const newTab = window.open('', '_blank');
  try {
    const token = await getToken();
    const response = await fetch(`${API_URL}?file=${encodeURIComponent(storageKey)}`, {
      headers: { Authorization: `Bearer ${token}` }
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => ({}));
      throw new Error(payload.error || 'No se pudo abrir el respaldo.');
    }
    const blobUrl = URL.createObjectURL(await response.blob());
    if (newTab) {
      newTab.location.href = blobUrl;
    } else {
      window.open(blobUrl, '_blank');
    }
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
  } catch (err) {
    if (newTab) newTab.close();
    throw err;
  }
};

// Documento del reclamo (tipo 'attachment': lo puede subir también el PA
// dueño del reclamo).
export const uploadClaimAttachment = (claimRecordId, file) =>
  uploadBackup({ file, claimId: claimRecordId, invoiceNumber: '', type: 'attachment' });
